import { Box, Flex } from '@chakra-ui/react';
import { Lightning01 } from '@kula-ai/icons-core';
import { automationChipHoverProps } from './automationChipStyles';
import type { ReactNode } from 'react';

// A stage `<select>` option. Options from the `ats_job_stages` endpoint carry an
// `automation` field (the active workflow rule, or null) — see
// `deriveStageAutomation`.
type StageOption = {
  automation?: unknown;
  [key: string]: unknown;
};

// Stage `<select>` option renderer: appends the automation marker (lightning
// icon on a purple `pastel-3-light` chip) when the stage has automation configured.
// react-select calls `formatOptionLabel` for BOTH the dropdown options and the
// selected value, so the marker shows in both. Shared by the CreateContact
// form, the import-candidates form, and the contacts add/import job+stage step
// so the marker is consistent across every stage picker.
export const renderStageOptionLabel = (
  option: StageOption,
  labelKey: string
): JSX.Element => {
  const hasAutomation = Boolean(option?.automation);
  return (
    <Flex alignItems="center" gap="6px" minW={0} w="100%">
      {/* Truncate so a long stage name can't overflow into the ⚡ marker in the
          selected value (react-select's SingleValue forces `white-space: nowrap`,
          so without this the text slides under the `ml="auto"` icon). */}
      <Box
        as="span"
        flex="1"
        minW={0}
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {option?.[labelKey] as ReactNode}
      </Box>
      {hasAutomation && (
        <Flex
          data-test-id="stage-automation-marker"
          alignItems="center"
          justifyContent="center"
          bg="pastel-3-light"
          borderRadius="6px"
          px="2px"
          py="4px"
          flexShrink={0}
          ml="auto"
          {...automationChipHoverProps}
        >
          <Lightning01
            size={14}
            color="var(--kula-colors-pastel-3-dark)"
            className="kula-icon"
          />
        </Flex>
      )}
    </Flex>
  );
};
